import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from '@storable/canaveral-components';
import useCurrentUser from '../../hooks/useCurrentUser';
import { MANAGE } from '../../../modules/authorization/actions';
import { FACILITY_PAGES, NON_FACILITY_PAGES } from '../../../modules/authorization/subjects';
import styles from './styles.scss';

const propTypes = {
  loading: PropTypes.bool,
  selectedMedium: PropTypes.string,
  restoreMedium: PropTypes.func.isRequired,
  setBanners: PropTypes.func.isRequired,
  setSelectedMedium: PropTypes.func.isRequired,
  data: PropTypes.shape({
    mediumToggleTrash: PropTypes.shape({
      medium: PropTypes.object,
      userErrors: PropTypes.array,
    }).isRequired,
  }),
  deleteMedium: PropTypes.func.isRequired,
  deleteLoading: PropTypes.bool,
  deleteData: PropTypes.shape({
    mediumDelete: PropTypes.shape({
      medium: PropTypes.object,
      userErrors: PropTypes.array,
    }).isRequired,
  }),
  deleteTrash: PropTypes.func.isRequired,
  deleteTrashLoading: PropTypes.bool,
  deleteTrashData: PropTypes.shape({
    mediumTrashDelete: PropTypes.shape({
      media: PropTypes.shape({
        medium: PropTypes.object,
      }),
      userErrors: PropTypes.array,
    }).isRequired,
  }),
};

const defaultProps = {
  loading: undefined,
  selectedMedium: undefined,
  data: undefined,
  deleteLoading: undefined,
  deleteData: undefined,
  deleteTrashLoading: undefined,
  deleteTrashData: undefined,
};

const MediaGalleryTrashActions = ({
  restoreMedium,
  loading,
  selectedMedium,
  data,
  setSelectedMedium,
  setBanners,
  deleteMedium,
  deleteLoading,
  deleteData,
  deleteTrash,
  deleteTrashLoading,
  deleteTrashData,
}) => {
  const [showTrashModal, setShowTrashModal] = useState(false);

  useEffect(() => {
    if (data) {
      if (data.mediumToggleTrash.userErrors.length) {
        setBanners({ errors: ['There was a problem restoring your image.  Please try again.'] });
      } else {
        setBanners({ success: 'Success!  Your image has been restored.' });
        setSelectedMedium(undefined);
      }
    }
  }, [data]);

  useEffect(() => {
    if (deleteData) {
      setShowTrashModal(false);
      if (deleteData.mediumDelete.userErrors.length) {
        setBanners({ errors: ['There was a problem permanently deleting your image.  Please try again.'] });
      } else {
        setBanners({ success: 'Success!  Your image has been permanently deleted.' });
        setSelectedMedium(undefined);
      }
    }
  }, [deleteData]);

  useEffect(() => {
    if (deleteTrashData) {
      setShowTrashModal(false);
      if (deleteTrashData.mediumDeleteTrash.userErrors.length) {
        setBanners({ errors: ['There was a problem permanently deleting the trash.  Please try again.'] });
      } else {
        setBanners({ success: 'Success!  Your images have been permanently deleted.' });
        setSelectedMedium(undefined);
      }
    }
  }, [deleteTrashData]);

  const currentUser = useCurrentUser();
  const unauthorized = currentUser.cannot(MANAGE, FACILITY_PAGES, NON_FACILITY_PAGES);

  const toggleShowTrashModal = () => setShowTrashModal(!showTrashModal);

  if (!selectedMedium) {
    return (
      <>
        <Button
          icon="trash"
          context="bare"
          className={styles.button}
          onClick={toggleShowTrashModal}
          disabled={unauthorized}
        >
          Empty Trash
        </Button>
        <Modal
          show={showTrashModal}
          onDismiss={toggleShowTrashModal}
        >
          <Modal.Header>
            <Modal.Title id="mediaGalleryTrashActions">
              Empty Trash
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Emptying the trash cannot be undone.  Are you sure you want to empty the trash?
          </Modal.Body>
          <Modal.Footer>
            <Button context="neutral" onClick={toggleShowTrashModal} disabled={loading || deleteTrashLoading}>
              Cancel
            </Button>
            <Button context="danger" onClick={deleteTrash} disabled={loading || deleteTrashLoading}>
              Empty
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Button context="bare" icon="restore" className={styles.button} onClick={restoreMedium}>
        Restore
      </Button>
      <Button context="bare" icon="trash" className={styles.button} onClick={toggleShowTrashModal}>
        Permanently Delete
      </Button>
      <Modal
        show={showTrashModal}
        onDismiss={toggleShowTrashModal}
      >
        <Modal.Header>
          <Modal.Title id="mediaGalleryTrashActions">
            Empty Trash
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Permanently deleting an image will remove it from your website admin. If
          you{"'"}re sure you want to delete this image, click <span className={styles.bold}>Delete</span>. To
          keep it, click <span className={styles.bold}>Cancel</span>.
        </Modal.Body>
        <Modal.Footer>
          <Button context="neutral" onClick={toggleShowTrashModal} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button context="danger" onClick={deleteMedium} disabled={deleteLoading || unauthorized}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

MediaGalleryTrashActions.propTypes = propTypes;
MediaGalleryTrashActions.defaultProps = defaultProps;

export default MediaGalleryTrashActions;
